import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { CodeProjectDetail, ExternalFindingRow, GateFinding, RegistryArtifactRow } from '../api/types'
import { CardCancello, CardVex, FinestraVex, type SoggettoVex } from '../components/CancelloSicurezza'
import { Card, EmptyState, ErrorBanner, PageSection, Spinner, StatCard } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { nonLicenziato, NotaLicenzaSbom, scaricaJson, TabellaComponenti } from '../components/Sbom'
import {
  BadgeGravita,
  BadgePipeline,
  etichettaFonte,
  etichettaRegistro,
  LinkEsterno,
  quando,
} from '../components/SoftwareProdotto'
import { FinestraAvvioRenovate, FinestraModifica, FinestraPipeline, NotaScrittura, StatoRunner, TabellaModifiche } from '../components/ModificheGitlab'
import type { TipoModifica } from '../components/ModificheGitlab'
import { usePermessiModuli } from '../auth/permessi'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * One GitLab project in «Software prodotto»: its pipelines with their jobs,
 * the current SBOM of each source (as that source gives it) with the
 * components and their vulnerabilities, the releases (tags) with their SBOM,
 * the artifacts it published and where they run, Renovate, and what the
 * external scanners report. See supply_chain_views.progetto.
 */

const n = (x: number) => x.toLocaleString(localeCorrente())

export default function ProgettoCodice() {
  const { id = '' } = useParams()
  const dati = useAsync(() => api.supplyProject(id), [id])
  const p = dati.data
  if (dati.error && nonLicenziato(dati.error)) return <Card><NotaLicenzaSbom /></Card>
  return (
    <>
      <header className="page__header">
        <div>
          <p className="field__hint" style={{ margin: 0 }}><Link to="/software-supply/projects">{t('Software prodotto')} · {t('Progetti')}</Link></p>
          <h1 className="page__title">{p?.path ?? t('Progetto')}</h1>
          {p && (
            <p className="page__subtitle">
              {p.web_url && <><LinkEsterno href={p.web_url}>{t('Apri in GitLab')}</LinkEsterno> · </>}
              {t('ramo principale')} <span className="mono">{p.default_branch || '—'}</span>
              {' · '}{t('connessione {nome}', { nome: p.connection })}
              {' · '}{t('letto {quando}', { quando: quando(p.synced_at) })}
            </p>
          )}
        </div>
      </header>
      <ErrorBanner error={dati.error} />
      {dati.loading && !p ? <Spinner /> : p && <Dettaglio p={p} onCambiato={() => void dati.reload()} />}
    </>
  )
}

type Azione = { tipo: 'pipeline'; azione: 'run' | 'retry' | 'cancel'; pipelineId?: number } | { tipo: 'mr'; kind: TipoModifica } | { tipo: 'renovate' }

function Dettaglio({ p, onCambiato }: { p: CodeProjectDetail; onCambiato: () => void }) {
  // The module's writing (gate rule, VEX) and, for GitLab writes, the connection's too.
  const scriveModulo = usePermessiModuli().scrive('software_supply')
  const scrive = scriveModulo && p.write.enabled
  const [azione, setAzione] = useState<Azione | null>(null)
  const [esito, setEsito] = useState<{ testo: string; url?: string } | null>(null)
  const fatto = (testo: string, url?: string) => { setAzione(null); setEsito({ testo, url }); onCambiato() }
  // ?gate=<decision> (the link in the CI report) opens that verdict.
  const [params] = useSearchParams()
  const [daValutare, setDaValutare] = useState<SoggettoVex | null>(null)
  const [giroVex, setGiroVex] = useState(0)
  const principale = p.sboms.find((s) => s.source === 'gitlab_pipeline' && s.ref === p.default_branch) ?? p.sboms[0]
  const inEsecuzione = p.artifacts.flatMap((a) => a.running)
  const r = p.renovate_detail
  const vulnerabili = useMemo<SoggettoVex[]>(() => {
    const out: SoggettoVex[] = []
    for (const c of principale?.component_list ?? []) {
      for (const v of c.vulnerabilities) {
        const id = v.cves[0] ?? v.id
        out.push({ vuln_id: id, aliases: [v.id, ...v.cves].filter((x) => x !== id), ecosystem: c.ecosystem, name: c.name,
          purl_base: '', purl: c.purl, version: c.version })
      }
    }
    return out
  }, [principale])
  const valutaVoce = (v: GateFinding) => setDaValutare({ vuln_id: v.cves[0] ?? v.vuln_id,
    aliases: [v.vuln_id, ...v.cves].filter((x) => x !== (v.cves[0] ?? v.vuln_id)), ecosystem: v.ecosystem, name: v.component,
    purl_base: v.purl_base, version: v.version })
  return (
    <>
      {p.sync_error && <div className="banner banner--error">{t('Ultima lettura non riuscita: {e}', { e: p.sync_error })}</div>}
      {esito && <div className="banner banner--info">{esito.testo}{esito.url && <> <LinkEsterno href={esito.url}>{t('Apri in GitLab')}</LinkEsterno></>}</div>}
      <div className="toolbar" style={{ alignItems: 'center' }}>
        <button type="button" disabled={!scrive} onClick={() => setAzione({ tipo: 'pipeline', azione: 'run' })}>{t('Avvia una pipeline')}</button>
        <button type="button" className={principale ? 'secondary' : ''} disabled={!scrive} onClick={() => setAzione({ tipo: 'mr', kind: 'sbom_job' })}>
          {principale ? t('Aggiorna il job SBOM') : t('Aggiungi il job SBOM')}
        </button>
        <NotaScrittura write={p.write} />
      </div>
      {azione?.tipo === 'pipeline' && <FinestraPipeline progetto={p} azione={azione.azione} pipelineId={azione.pipelineId} onChiudi={() => setAzione(null)} onFatto={fatto} />}
      {azione?.tipo === 'mr' && <FinestraModifica progetto={p} tipo={azione.kind} onChiudi={() => setAzione(null)} onFatto={fatto} />}
      {azione?.tipo === 'renovate' && <FinestraAvvioRenovate progetto={p} onChiudi={() => setAzione(null)} onFatto={fatto} />}
      <div className="stats">
        <StatCard label={t('Componenti nell’SBOM')} value={principale ? n(principale.components) : '—'} />
        <StatCard label={t('Vulnerabili')} value={principale ? n(principale.vulnerable) : '—'}
          tone={principale?.vulnerable ? 'warn' : 'ok'} />
        <StatCard label={t('Sfruttate attivamente')} value={principale ? n(principale.exploited) : '—'}
          tone={principale?.exploited ? 'error' : 'ok'} />
        <StatCard label={t('Non valutati')} value={principale ? n(principale.unevaluated) : '—'}
          hint={t('ecosistemi senza catalogo')} />
        <StatCard label={t('MR di sicurezza di Renovate')} value={n(r.security_mrs ?? 0)} />
        <StatCard label={t('Artefatti pubblicati')} value={n(p.artifacts.length)} />
        <StatCard label={t('Container in esecuzione')} value={n(inEsecuzione.length)} />
      </div>
      {!principale && (
        <Card>
          <EmptyState title={t('Nessun SBOM per questo progetto')}
            hint={t('Le pipeline non lasciano un SBOM CycloneDX: aggiungi alla pipeline il job che lo produce (censo-sbom.cdx.json, oppure un report cyclonedx).')} />
        </Card>
      )}
      <PageSection id="sbom"><SbomPerFonte p={p} /></PageSection>
      <PageSection id="pipeline"><Pipeline p={p} scrive={scrive} onAzione={(a, id) => setAzione({ tipo: 'pipeline', azione: a, pipelineId: id })} /></PageSection>
      <PageSection id="rilasci"><Rilasci p={p} /></PageSection>
      <PageSection id="artefatti"><Artefatti righe={p.artifacts} /></PageSection>
      <PageSection id="renovate">
        <Card title={t('Renovate')}>
          {r.file ? (
            <>
              <p style={{ marginTop: 0 }}>
                {t('Configurazione in')} <span className="mono">{r.file}</span>{' · '}
                {r.osv_alerts === true ? t('avvisi OSV accesi') : r.osv_alerts === false ? t('avvisi OSV spenti: Renovate non apre MR per le vulnerabilità') : t('avvisi OSV non determinabili (preset o configurazione del runner)')}
              </p>
              {(r.open_mrs ?? []).length === 0 ? <p className="text-muted">{t('Nessuna merge request di Renovate aperta.')}</p> : (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {(r.open_mrs ?? []).map((m) => (
                    <li key={m.iid}>
                      <LinkEsterno href={m.web_url}>!{m.iid}</LinkEsterno> {m.title}
                      {m.security && <> <span className="badge badge--warn">{t('sicurezza')}</span></>}
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : <p className="text-muted" style={{ margin: 0 }}>{t('Renovate non è configurato in questo progetto.')}</p>}
          <p className="field__hint"><StatoRunner r={p.renovate_runner} /></p>
          <div className="toolbar" style={{ marginBottom: 0 }}>
            {!r.file && <button type="button" disabled={!scrive} onClick={() => setAzione({ tipo: 'mr', kind: 'renovate_onboarding' })}>{t('Proponi la configurazione di Renovate')}</button>}
            {r.file && r.osv_alerts !== true && <button type="button" className="secondary" disabled={!scrive || !(r.file ?? '').endsWith('.json') || (r.file ?? '').startsWith('package.json')}
              onClick={() => setAzione({ tipo: 'mr', kind: 'renovate_osv' })}>{t('Accendi gli avvisi OSV')}</button>}
            {r.file && <button type="button" className="secondary" disabled={!scrive || !p.renovate_runner?.on_demand}
              title={p.renovate_runner?.on_demand ? undefined : t('Serve il runner con l’avvio su richiesta di CENSO')}
              onClick={() => setAzione({ tipo: 'renovate' })}>{t('Avvia Renovate adesso')}</button>}
          </div>
        </Card>
      </PageSection>
      <PageSection id="cancello">
        <CardCancello key={`g${giroVex}`} progetto={p} scrive={scriveModulo} scriveGitlab={scrive} aperta={params.get('gate')}
          onAggiungiJob={() => setAzione({ tipo: 'mr', kind: 'gate_job' })} onValuta={valutaVoce} />
      </PageSection>
      <PageSection id="vex"><CardVex key={`v${giroVex}`} progetto={p} vulnerabili={vulnerabili} scrive={scriveModulo} /></PageSection>
      {daValutare && <FinestraVex progettoId={p.id} soggetto={daValutare} onChiudi={() => setDaValutare(null)}
        onFatto={() => { setDaValutare(null); setGiroVex((x) => x + 1) }} />}
      <PageSection id="esterni"><Esterni righe={p.external_findings} /></PageSection>
      {p.changes.length > 0 && (
        <PageSection id="modifiche">
          <Card title={t('Azioni di CENSO in GitLab')}><TabellaModifiche righe={p.changes} conProgetto={false} /></Card>
        </PageSection>
      )}
      <Card title={t('Pipeline e merge')}>
        <p style={{ margin: 0 }}>
          {p.merge_requires_pipeline === true ? t('«Pipelines must succeed» è acceso: una pipeline fallita blocca il merge.')
            : p.merge_requires_pipeline === false ? t('«Pipelines must succeed» è spento: una pipeline fallita non blocca il merge.')
              : t('Non si sa se «Pipelines must succeed» sia acceso.')}
          {p.auto_devops && ` ${t('Auto DevOps è attivo.')}`}
          {p.ci_config_path && ` ${t('Il file della pipeline è {f}.', { f: p.ci_config_path })}`}
        </p>
      </Card>
    </>
  )
}

function SbomPerFonte({ p }: { p: CodeProjectDetail }) {
  const fonti = p.sboms.map((s) => s.source).filter((v, i, a) => a.indexOf(v) === i)
  const [fonte, setFonte] = useState(fonti[0] ?? '')
  const [errore, setErrore] = useState<unknown>(null)
  if (fonti.length === 0) return null
  const correnti = p.sboms.filter((s) => s.source === fonte)
  return (
    <Card title={t('SBOM')}>
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 6 }} role="tablist">
        {fonti.map((f) => (
          <button key={f} type="button" role="tab" aria-selected={fonte === f} className={fonte === f ? '' : 'secondary'}
            onClick={() => setFonte(f)}>{etichettaFonte(f)}</button>
        ))}
      </div>
      <ErrorBanner error={errore} />
      {correnti.map((s) => (
        <details key={s.id} open={correnti.length === 1} className="card__inset" style={{ marginBottom: 8 }}>
          <summary>
            <strong className="mono">{s.ref || s.subject_name}</strong>{s.is_tag && <> <span className="badge badge--info">{t('rilascio')}</span></>}{' '}
            <small className="text-muted">
              {s.format} · {t('{c} componenti', { c: s.components })} · {t('letto {quando}', { quando: quando(s.fetched_at) })}
            </small>
            {s.vulnerable > 0 && <> <span className="badge badge--warn">{t('{n} vulnerabili', { n: s.vulnerable })}</span></>}
            {s.exploited > 0 && <> <span className="badge badge--error">{t('{n} sfruttate', { n: s.exploited })}</span></>}
          </summary>
          <div className="toolbar" style={{ alignItems: 'center' }}>
            {s.has_document && (
              <button type="button" className="linklike"
                onClick={() => scaricaJson(api.supplySbomDocumentPath(s.id), `${(p.path || 'sbom').replace(/[^A-Za-z0-9._-]+/g, '-')}-${s.ref}.cdx.json`).catch(setErrore)}>
                {t('Scarica il documento originale')}
              </button>
            )}
            {s.unevaluated > 0 && <small className="text-muted">{t('{n} componenti di ecosistemi senza catalogo: non valutati.', { n: s.unevaluated })}</small>}
          </div>
          {s.component_list.length ? <TabellaComponenti componenti={s.component_list} />
            : <p className="text-muted">{t('Nessun componente in questo SBOM.')}</p>}
        </details>
      ))}
    </Card>
  )
}

function Pipeline({ p, scrive, onAzione }: {
  p: CodeProjectDetail; scrive: boolean; onAzione: (a: 'retry' | 'cancel', id: number) => void
}) {
  type Riga = CodeProjectDetail['pipelines'][number]
  const colonne: Column<Riga>[] = [
    { key: 'id', header: t('Pipeline'), value: (r) => r.id, render: (r) => (r.web_url ? <LinkEsterno href={r.web_url}>#{r.id}</LinkEsterno> : <>#{r.id}</>) },
    { key: 'ref', header: t('Ramo o tag'), value: (r) => r.ref, render: (r) => (
      <><span className="mono">{r.ref}</span>{r.is_tag && <> <span className="badge badge--info">{t('tag')}</span></>}<br /><small className="mono text-muted">{r.sha.slice(0, 8)}</small></>
    ) },
    { key: 'status', header: t('Stato'), value: (r) => r.status, render: (r) => <BadgePipeline stato={r.status} /> },
    { key: 'created_at', header: t('Creata'), type: 'date', value: (r) => r.created_at ?? '', render: (r) => <small>{quando(r.created_at)}</small> },
    { key: 'azioni', header: '', filter: false, sort: false, nowrap: true, render: (r) => (
      <>
        {['failed', 'canceled'].includes(r.status) && <button type="button" className="secondary" disabled={!scrive} onClick={() => onAzione('retry', r.id)}>{t('Riprova')}</button>}
        {['running', 'pending', 'created', 'waiting_for_resource', 'preparing', 'scheduled'].includes(r.status) && (
          <button type="button" className="secondary" disabled={!scrive} onClick={() => onAzione('cancel', r.id)}>{t('Annulla')}</button>
        )}
      </>
    ) },
    { key: 'jobs', header: t('Job'), value: (r) => r.jobs.length, render: (r) => (
      r.jobs.length ? <small>{r.jobs.map((j) => (
        <span key={j.id} style={{ marginRight: 8 }}><BadgePipeline stato={j.status} /> {j.name}{j.cyclonedx && ' · SBOM'}</span>
      ))}</small> : <span className="text-muted">—</span>
    ) },
  ]
  return (
    <Card title={t('Pipeline')}>
      <DataTable rows={p.pipelines} rowKey={(r) => String(r.id)} columns={colonne} conteggio={{ nome: t('pipeline') }}
        emptyText={t('Nessuna pipeline letta.')} />
    </Card>
  )
}

function Rilasci({ p }: { p: CodeProjectDetail }) {
  const [errore, setErrore] = useState<unknown>(null)
  if (p.releases.length === 0) return null
  return (
    <Card title={t('Rilasci (tag) con SBOM')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Ogni tag con il suo SBOM è conservato: è la documentazione del rilascio (per il CRA, dieci anni o il periodo di supporto se più lungo).')}
      </p>
      <ErrorBanner error={errore} />
      <DataTable rows={p.releases} rowKey={(s) => s.id} conteggio={{ nome: t('rilasci') }} columns={[
        { key: 'ref', header: t('Tag'), mono: true, value: (s) => s.ref },
        { key: 'components', header: t('Componenti'), type: 'number', value: (s) => s.components },
        { key: 'vulnerable', header: t('Vulnerabili'), type: 'number', value: (s) => s.vulnerable,
          render: (s) => (s.vulnerable ? <span className="badge badge--warn">{s.vulnerable}</span> : '—') },
        { key: 'exploited', header: t('Sfruttate'), type: 'number', value: (s) => s.exploited,
          render: (s) => (s.exploited ? <span className="badge badge--error">{s.exploited}</span> : '—') },
        { key: 'fetched_at', header: t('Letto'), type: 'date', value: (s) => s.fetched_at ?? '', render: (s) => <small>{quando(s.fetched_at)}</small> },
        { key: 'doc', header: '', filter: false, sort: false, render: (s) => (s.has_document ? (
          <button type="button" className="linklike"
            onClick={() => scaricaJson(api.supplySbomDocumentPath(s.id), `${(p.path || 'sbom').replace(/[^A-Za-z0-9._-]+/g, '-')}-${s.ref}.cdx.json`).catch(setErrore)}>
            {t('Scarica')}
          </button>) : null) },
      ]} />
    </Card>
  )
}

function Artefatti({ righe }: { righe: RegistryArtifactRow[] }) {
  if (righe.length === 0) return null
  return (
    <Card title={t('Artefatti pubblicati e dove girano')}>
      <DataTable rows={righe} rowKey={(r) => r.id} conteggio={{ nome: t('artefatti') }} columns={[
        { key: 'name', header: t('Artefatto'), value: (r) => `${r.name} ${r.version}`, render: (r) => (
          <><strong>{r.name}</strong> <span className="mono">{r.version}</span><br /><small className="text-muted">{etichettaRegistro(r.provider)} · {r.repository}</small></>
        ) },
        { key: 'digest', header: t('Impronta'), value: (r) => r.docker_digest || r.sha256,
          render: (r) => <small className="mono">{(r.docker_digest || r.sha256).replace('sha256:', '').slice(0, 16) || '—'}</small> },
        { key: 'running', header: t('In esecuzione su'), value: (r) => r.running.length, render: (r) => (r.running.length ? (
          <small>{r.running.map((c) => <span key={c.ci_id + c.container}><Link to={`/cis/${c.ci_id}`}>{c.ci_name}</Link> · {c.container} <span className="text-muted">({c.state})</span><br /></span>)}</small>
        ) : <span className="text-muted">—</span>) },
        { key: 'created_at', header: t('Pubblicato'), type: 'date', value: (r) => r.created_at ?? '', render: (r) => <small>{quando(r.created_at)}</small> },
      ]} />
    </Card>
  )
}

function Esterni({ righe }: { righe: ExternalFindingRow[] }) {
  const fonti = righe.map((r) => r.source).filter((v, i, a) => a.indexOf(v) === i)
  const [fonte, setFonte] = useState(fonti[0] ?? '')
  if (righe.length === 0) return null
  return (
    <Card title={t('Vulnerabilità secondo le altre fonti')}>
      <p className="field__hint" style={{ marginTop: 0 }}>{t('Così come le riporta ciascuna fonte, senza ricalcoli.')}</p>
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 6 }} role="tablist">
        {fonti.map((f) => (
          <button key={f} type="button" role="tab" aria-selected={fonte === f} className={fonte === f ? '' : 'secondary'}
            onClick={() => setFonte(f)}>{etichettaFonte(f)} ({righe.filter((r) => r.source === f).length})</button>
        ))}
      </div>
      <DataTable rows={righe.filter((r) => r.source === fonte)} rowKey={(r) => r.id} conteggio={{ nome: t('vulnerabilità') }} columns={[
        { key: 'vuln_id', header: t('Avviso'), value: (r) => r.vuln_id, render: (r) => (r.url ? <LinkEsterno href={r.url}><span className="mono">{r.vuln_id}</span></LinkEsterno> : <span className="mono">{r.vuln_id}</span>) },
        { key: 'name', header: t('Componente'), value: (r) => `${r.name} ${r.version}` },
        { key: 'severity', header: t('Gravità'), value: (r) => r.severity, render: (r) => <BadgeGravita g={r.severity} /> },
        { key: 'fixed', header: t('Corretta in'), mono: true, value: (r) => r.fixed },
        { key: 'subject_label', header: t('Riferimento della fonte'), value: (r) => r.subject_label },
      ]} />
    </Card>
  )
}
